import { buttonLink } from '@insify/css/classes.module.css';
import { useAnimatedHeight } from '@insify/react-hooks';
import { TrackedButton, TrackedExpandableContext } from '@objectiv/tracker-react';
import clsx from 'clsx';
import React, { createContext, useContext } from 'react';

import { textSm } from '../../typography';
import { Toggle } from '../Toggle';
import { CardDescription, CardHeading } from '../card-components';

import styles from './Panel.module.css';

import type { ToggleProps } from '../Toggle';
import type { ComponentPropsWithRef } from 'react';

export type PanelProps = ToggleProps & {
  className?: string;
  icon?: React.ReactNode;
  header?: string;
  description?: React.ReactNode;
  showToggle?: boolean;
  details?: React.ReactNode;
};

type InnerPanelProps = Omit<PanelProps, 'trackingId'> &
  Omit<ReturnType<typeof useAnimatedHeight>, 'ref'> & {
    detailsRef: React.RefObject<HTMLDivElement>;
  };

const PanelContext = createContext<{ toggle: () => void; isOpen: boolean; detailsId: string } | undefined>(undefined);

export const PanelAnchor = ({ className, ...props }: React.ComponentPropsWithoutRef<'button'>): JSX.Element => {
  const panelContextValue = useContext(PanelContext);
  if (!panelContextValue) throw new Error('PanelAnchor must be used inside a PanelContext.');
  return (
    <TrackedButton
      objectiv={{ id: 'trigger' }}
      type='button'
      className={clsx(buttonLink, textSm, styles.panelAnchor, className)}
      aria-controls={panelContextValue.detailsId}
      onClick={panelContextValue.toggle}
      aria-expanded={panelContextValue.isOpen}
      {...props}
    />
  );
};

const InnerPanel = React.forwardRef<HTMLInputElement, InnerPanelProps>(
  (
    { toggle, isOpen, className, icon, header, description, showToggle = true, details, detailsRef, ...toggleProps },
    ref,
  ) => {
    const detailsId = `${toggleProps.id}-details`;
    return (
      <PanelContext.Provider value={{ toggle, isOpen, detailsId }}>
        <div className={clsx(styles.panel, textSm, className)}>
          <div className={styles.content}>
            {Boolean(icon) && (
              <div className={styles.iconContainer} role='img'>
                {icon}
              </div>
            )}
            <div className={styles.text}>
              {Boolean(header) && <CardHeading data-testid={`${toggleProps.id}-header`}>{header}</CardHeading>}
              <CardDescription>{description}</CardDescription>
            </div>
            {showToggle && (
              <Toggle
                className={styles.toggle}
                ref={ref}
                data-testid={`${toggleProps.id}-toggle`}
                trackingId='toggle'
                {...toggleProps}
              />
            )}
          </div>
          {Boolean(details) && (
            <div ref={detailsRef} data-testid={detailsId} id={detailsId}>
              <div className={clsx(textSm, styles.detailsContent)}>{details}</div>
            </div>
          )}
        </div>
      </PanelContext.Provider>
    );
  },
);

export const Panel = React.forwardRef<HTMLInputElement, PanelProps>(({ trackingId, ...props }, ref): JSX.Element => {
  const { ref: animatedHeightRef, toggle: toggleDetails, isOpen } = useAnimatedHeight<HTMLDivElement>();

  return (
    <TrackedExpandableContext<ComponentPropsWithRef<typeof InnerPanel>>
      detailsRef={animatedHeightRef}
      ref={ref}
      objectiv={{
        id: trackingId,
        isVisible: isOpen,
        Component: InnerPanel,
      }}
      toggle={toggleDetails}
      isOpen={isOpen}
      {...props}
    />
  );
});
